import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalTransition,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const i18n = defineMessages({
	replaceApprovalButtonLabel: {
		id: 'approvals.replace-approval.modal.replace-approval.button.label',
		defaultMessage: 'Replace approval',
		description: 'Label for a button to confirm replacing the approval by publishing',
	},
	modalHeading: {
		id: 'approvals.replace-approval.modal.heading',
		defaultMessage: 'Update and replace approval?',
		description:
			'Heading for the replace approval modal shown when publishing would override an existing approval',
	},
	modalDescription: {
		id: 'approvals.replace-approval.modal.description',
		defaultMessage:
			'The current version of this page has an active or completed review. Publishing a new version will replace it.',
		description: 'Description explaining the consequences of publishing over an existing approval',
	},
});

const styles = cssMap({
	footerRow: {
		justifyContent: 'flex-end',
		gap: token('space.150'),
		marginTop: token('space.200'),
	},
});

type ReplaceApprovalModalFooterOptionsProps = {
	onConfirm: () => void;
};

const ReplaceApprovalModalFooterOptions: FC<ReplaceApprovalModalFooterOptionsProps> = ({
	onConfirm,
}) => {
	return (
		<Inline xcss={styles.footerRow}>
			<Button appearance="warning" onClick={onConfirm}>
				<FormattedMessage {...i18n.replaceApprovalButtonLabel} />
			</Button>
		</Inline>
	);
};

type ReplaceApprovalModalProps = {
	onConfirm: () => void;
	onClose: () => void;
};

export const ReplaceApprovalModal: FC<ReplaceApprovalModalProps> = ({ onConfirm, onClose }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<ModalTransition>
			<Modal onClose={onClose}>
				<ModalHeader hasCloseButton>
					<ModalTitle appearance="warning">
						<FormattedMessage {...i18n.modalHeading} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<FormattedMessage {...i18n.modalDescription} />
				</ModalBody>
				<ModalFooter>
					<ReplaceApprovalModalFooterOptions onConfirm={onConfirm} />
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
